import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import TextField from '@mui/material/TextField/TextField';
import IconButton from '@mui/material/IconButton/IconButton';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupsIcon from '@mui/icons-material/Groups';
import { alpha, createTheme, Theme, ThemeProvider, useTheme } from '@mui/material/styles';
import { newObjectId } from '../../utils/ids';
import { LOOK } from '../../theme';

// the category cards are dark, so they stand out from the rest of the form: the look's terminal black, with its green
const CARD = LOOK.terminal;
const darkCards = (theme: Theme) => createTheme(theme, {
    palette: {
        mode: 'dark',
        background: { paper: CARD, default: CARD },
        text: { primary: LOOK.cream, secondary: '#c9bfb2' },
        divider: alpha('#ffffff', 0.14),
        action: { active: '#e2e8f0', hover: alpha('#ffffff', 0.08), selected: alpha('#ffffff', 0.14) },
        // the icon's green, glowing on the terminal
        primary: { main: LOOK.green, contrastText: LOOK.terminal }
    },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                // the form's inputs are white; here they're a shade lighter than the card
                root: { backgroundColor: alpha('#ffffff', 0.06) }
            }
        }
    }
});

export type CategoryMeta = { _id?: string, judgeOnly: boolean, description: string };

type Value = { categories: string[], categoryMeta: CategoryMeta[] };

// one tap to start from: what organizers keep typing (Charles renames "Impact" to "Customer impact" every run)
const SUGGESTIONS: Array<{ label: string, description: string }> = [
    { label: 'Customer impact', description: 'Would a customer notice, or pay for it?' },
    { label: 'Feasibility', description: 'Could we ship it? How much works today?' },
    { label: 'Creativity', description: 'Is it new, or a fresh take on an old problem?' },
    { label: 'Demo', description: 'Did the demo show it working?' }
];

export { withoutBlankCategories } from '../../utils/categories';

/**
 * Judging categories: one compact row each (grip to drag, name, who scores it, remove), the description optional
 * underneath. Adding puts a row in place with the cursor in it; removing offers Undo instead of asking first.
 */
export const CategoryEditor = ({ value, onChange }: { value: Value, onChange: (next: Value) => void }) => {
    const { categories, categoryMeta } = value;
    const theme = useTheme();
    const cardTheme = useMemo(() => darkCards(theme), [theme]);
    const [focusId, setFocusId] = useState<string | null>(null);
    const [removed, setRemoved] = useState<{ index: number, name: string, meta: CategoryMeta } | null>(null);
    const inputs = useRef(new Map<string, HTMLInputElement | null>());

    useEffect(() => {
        if (focusId) {
            inputs.current.get(focusId)?.focus();
            setFocusId(null);
        }
    }, [focusId, categories.length]);

    const idOf = (index: number) => categoryMeta[index]?._id || `${index}`;
    const set = (cats: string[], metas: CategoryMeta[]) => onChange({ categories: cats, categoryMeta: metas });

    const add = (label = '', description = '') => {
        const _id = newObjectId();
        set([...categories, label], [...categoryMeta, { _id, judgeOnly: false, description }]);
        if (!label) {
            setFocusId(_id);
        }
        setRemoved(null);
    };
    const remove = (index: number) => {
        setRemoved({ index, name: categories[index], meta: categoryMeta[index] });
        set(categories.filter((_, i) => i !== index), categoryMeta.filter((_, i) => i !== index));
    };
    const undo = () => {
        if (!removed) return;
        const cats = [...categories];
        const metas = [...categoryMeta];
        cats.splice(removed.index, 0, removed.name);
        metas.splice(removed.index, 0, removed.meta);
        set(cats, metas);
        setRemoved(null);
    };
    const onDragEnd = (result: DropResult<string>) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const cats = [...categories];
        const metas = [...categoryMeta];
        const [cat] = cats.splice(result.source.index, 1);
        const [meta] = metas.splice(result.source.index, 1);
        cats.splice(result.destination.index, 0, cat);
        metas.splice(result.destination.index, 0, meta);
        set(cats, metas);
    };

    const duplicate = (index: number) => {
        const name = categories[index].trim().toLowerCase();
        return !!name && categories.some((c, i) => i !== index && c.trim().toLowerCase() === name);
    };
    const unused = SUGGESTIONS.filter(s => !categories.some(c => c.trim().toLowerCase() === s.label.toLowerCase()));

    return (
        <Stack spacing={1.5}>
            {
                categories.length === 0 && <Typography color="text.secondary">No categories yet. Judges score each team on every category, 1 to 10.</Typography>
            }
            <ThemeProvider theme={cardTheme}>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="categories">
                    {(drop) => (
                        <Stack spacing={1} ref={drop.innerRef} {...drop.droppableProps}>
                            {categories.map((category, index) => (
                                // keyed by id, not name, so typing a name doesn't remount the field
                                <Draggable key={idOf(index)} draggableId={idOf(index)} index={index}>
                                    {(drag, snapshot) => {
                                        const meta = categoryMeta[index] || { judgeOnly: false, description: '' };
                                        return (
                                            <Box ref={drag.innerRef} {...drag.draggableProps}
                                                sx={{
                                                    display: 'flex', gap: 1, alignItems: 'flex-start',
                                                    p: 1.5, pl: 0.5, borderRadius: '12px',
                                                    border: theme => `1px solid ${theme.palette.divider}`,
                                                    // a lit top edge over a navy fade, on a layered shadow: raised off the form
                                                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.05)}, transparent 60%), ${CARD}`,
                                                    color: 'text.primary',
                                                    boxShadow: snapshot.isDragging
                                                        ? `inset 0 1px 0 ${alpha('#ffffff', 0.1)}, 0 4px 8px rgba(15, 23, 42, 0.25), 0 24px 48px -8px rgba(15, 23, 42, 0.6)`
                                                        : `inset 0 1px 0 ${alpha('#ffffff', 0.1)}, 0 1px 2px rgba(15, 23, 42, 0.2), 0 10px 24px -6px rgba(15, 23, 42, 0.45)`
                                                }}>
                                                <Tooltip title="Drag to reorder">
                                                    <Box {...drag.dragHandleProps} aria-label={`Drag to reorder: ${category || 'new category'}`}
                                                        sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', px: 0.5, color: 'text.secondary', cursor: 'grab', borderRadius: '8px', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' }, '&:active': { cursor: 'grabbing' } }}>
                                                        <DragIndicatorIcon />
                                                    </Box>
                                                </Tooltip>
                                                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                        <TextField size="small" label={`Category ${index + 1}`} value={category} sx={{ flex: 1, minWidth: 180 }}
                                                            placeholder="e.g. Customer impact"
                                                            inputRef={el => { inputs.current.set(idOf(index), el); }}
                                                            inputProps={{ maxLength: 60 }}
                                                            error={duplicate(index)} helperText={duplicate(index) ? 'Already a category' : undefined}
                                                            onChange={e => set(categories.map((c, i) => i === index ? e.target.value : c), categoryMeta)} />
                                                        <Tooltip title={meta.judgeOnly ? 'Only judges score this. Click to let participants score it too.' : 'Judges and participants (when they score) score this. Click to make it judges only.'}>
                                                            <Chip
                                                                icon={meta.judgeOnly ? <GavelIcon /> : <GroupsIcon />}
                                                                label={meta.judgeOnly ? 'Judges only' : 'Everyone scores'}
                                                                color={meta.judgeOnly ? 'primary' : 'default'}
                                                                variant={meta.judgeOnly ? 'filled' : 'outlined'}
                                                                role="switch"
                                                                aria-checked={meta.judgeOnly}
                                                                aria-label={`Judges only: ${category || 'new category'}`}
                                                                sx={{ width: 152, justifyContent: 'flex-start' }}
                                                                onClick={() => set(categories, categoryMeta.map((m, i) => i === index ? { ...m, judgeOnly: !m.judgeOnly } : m))} />
                                                        </Tooltip>
                                                    </Stack>
                                                    <TextField size="small" label="What judges look for (optional)" value={meta.description} multiline minRows={1} maxRows={4} fullWidth
                                                        onChange={e => set(categories, categoryMeta.map((m, i) => i === index ? { ...m, description: e.target.value } : m))} />
                                                </Stack>
                                                <IconButton aria-label={`Remove category: ${category || 'new category'}`} onClick={() => remove(index)}>
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Box>
                                        );
                                    }}
                                </Draggable>
                            ))}
                            {drop.placeholder}
                        </Stack>
                    )}
                </Droppable>
            </DragDropContext>
            </ThemeProvider>
            {
                removed && <Alert severity="info" action={<Button color="inherit" size="small" onClick={undo}>Undo</Button>} onClose={() => setRemoved(null)}>
                    Removed <b>{removed.name || 'the new category'}</b>.
                </Alert>
            }
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => add()}>Add category</Button>
                {
                    unused.length > 0 && <>
                        <Typography variant="body2" color="text.secondary">or add:</Typography>
                        {unused.map(s => <Chip key={s.label} size="small" variant="outlined" icon={<AddIcon />} label={s.label} onClick={() => add(s.label, s.description)} />)}
                    </>
                }
            </Stack>
        </Stack>
    );
}
